import React from 'react';
import { Circle, Ellipse, G, Line, Polygon, Rect, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { DT } from '../tokens';

/** V = 4005√(VP) — pitot probe in duct, manometer with fluid differential, velocity profile. */
export function DiagramVelocityOfAir() {
  const dx = 30, dy = 120, dw = 200, dh = 70;
  const cy = dy + dh / 2;
  const probeX = dx + dw * 0.45;

  // Velocity streamlines (parabolic-ish)
  const lines = Array.from({ length: 7 }, (_, i) => {
    const t = (i + 0.5) / 7;
    const yp = dy + 5 + t * (dh - 10);
    const len = 50 * (1 - Math.pow((t - 0.5) * 2, 2));
    return { yp, len };
  });

  // Manometer position (top right)
  const mx = 280, my = 30, mw = 30, mh = 90;
  // Fluid level: total side lower, static side higher (differential = VP)
  const totalLvl = my + 30;
  const staticLvl = my + 70;

  return (
    <DiagramCanvas>
      {/* Duct */}
      <Rect x={dx} y={dy} width={dw} height={dh} fill={DT.fill} stroke={DT.stroke} strokeWidth="1.5" />
      {/* Velocity profile */}
      {lines.map((l, i) => (
        <G key={i}>
          <Line x1={dx + 8} y1={l.yp} x2={dx + 8 + l.len} y2={l.yp}
            stroke={DT.arrow} strokeWidth="1.1" opacity={0.8} />
          <Polygon
            points={`${dx + 8 + l.len},${l.yp} ${dx + 4 + l.len},${l.yp - 2} ${dx + 4 + l.len},${l.yp + 2}`}
            fill={DT.arrow} opacity={0.8}
          />
        </G>
      ))}

      {/* Pitot probe — L-shape facing upstream */}
      {/* Stem from manometer down into duct */}
      <Line x1={probeX} y1={dy - 40} x2={probeX} y2={cy + 6}
        stroke={DT.leader} strokeWidth="3" />
      {/* Total-pressure tip (impact) — points upstream */}
      <Line x1={probeX - 14} y1={cy} x2={probeX} y2={cy}
        stroke={DT.leader} strokeWidth="3" />
      <Circle cx={probeX - 14} cy={cy} r={2.5} fill={DT.varColor} />
      {/* Static pressure side ports (small holes) */}
      <Circle cx={probeX} cy={cy + 4} r={1} fill={DT.varColor} />
      <Circle cx={probeX} cy={cy - 4} r={1} fill={DT.varColor} />

      {/* Tubing from pitot to manometer */}
      <Line x1={probeX} y1={dy - 40} x2={probeX + 5} y2={dy - 50}
        stroke={DT.leader} strokeWidth="1.2" />
      <Line x1={probeX + 5} y1={dy - 50} x2={mx + 5} y2={my + mh + 8}
        stroke={DT.leader} strokeWidth="1.2" />
      <Line x1={probeX + 4} y1={dy - 36} x2={mx + 25} y2={my + mh + 8}
        stroke={DT.leader} strokeWidth="1.2" strokeDasharray="3,2" />

      {/* Manometer (U-tube) */}
      <Rect x={mx} y={my} width={mw} height={mh} fill="none" stroke={DT.stroke} strokeWidth="1.5" />
      {/* Inner U separator (shape) */}
      <Line x1={mx + 8} y1={my} x2={mx + 8} y2={my + mh - 12} stroke={DT.stroke} strokeWidth="1" />
      <Line x1={mx + mw - 8} y1={my} x2={mx + mw - 8} y2={my + mh - 12} stroke={DT.stroke} strokeWidth="1" />
      {/* Fluid (left = total, right = static) */}
      <Rect x={mx + 1} y={totalLvl} width={7} height={my + mh - totalLvl - 1} fill={DT.varColor} opacity={0.7} />
      <Rect x={mx + 1} y={my + mh - 12} width={mw - 2} height={11} fill={DT.varColor} opacity={0.7} />
      <Rect x={mx + mw - 8} y={staticLvl} width={7} height={my + mh - staticLvl - 1} fill={DT.varColor} opacity={0.7} />
      {/* Differential indicator */}
      <Line x1={mx - 6} y1={totalLvl} x2={mx - 6} y2={staticLvl} stroke={DT.varColor} strokeWidth="1.5" />
      <Polygon points={`${mx - 6},${totalLvl} ${mx - 9},${totalLvl + 4} ${mx - 3},${totalLvl + 4}`} fill={DT.varColor} />
      <Polygon points={`${mx - 6},${staticLvl} ${mx - 9},${staticLvl - 4} ${mx - 3},${staticLvl - 4}`} fill={DT.varColor} />
      <SvgText x={mx - 10} y={(totalLvl + staticLvl) / 2 + 3} fill={DT.varColor}
        fontSize="10" fontFamily={DT.mono} textAnchor="end" fontWeight="700">VP</SvgText>

      {/* Labels */}
      <SvgText x={mx + mw / 2} y={my - 4} fill={DT.desc} fontSize="9"
        fontFamily={DT.mono} textAnchor="middle">manometer</SvgText>

      <LeaderLine x1={probeX - 14} y1={cy} x2={probeX - 60} y2={210} varName="Pt" desc="Total press." anchor="middle" />
      <LeaderLine x1={probeX} y1={cy + 4} x2={probeX + 70} y2={210} varName="Ps" desc="Static press." anchor="middle" />
      <LeaderLine x1={dx + 35} y1={cy + 18} x2={dx + 5} y2={70} varName="V" desc="Velocity (FPM)" anchor="start" />

      <FormulaTag formula="V = 4005 × √VP   (std. air, in. w.g.)" />
    </DiagramCanvas>
  );
}
